import { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import MainContainer from '../components/ui_components/MainContainer';
import {
  apiGetMyNotifications,
  apiMarkNotificationAsRead,
  apiDeleteNotification,
} from '../components/ui_components/ApiList';
import colors from '../styles/colors';
import type { Notification } from '../types';
import { parseWrestlerTags } from '../utils/wrestlerParser';

const PageWrapper = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding: 24px 20px;
`;

const PageTitle = styled.h1`
  color: white;
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 2px solid ${colors.bgColorBordeaux};
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const MessageCard = styled.li<{ $expanded?: boolean }>`
  background: ${colors.bgColorBlack2};
  border: 2px solid ${colors.bgColorBordeaux};
  border-radius: 8px;
  overflow: hidden;
  transition: border-color 0.2s, background 0.2s;

  &:hover {
    border-color: rgba(246, 39, 83, 0.8);
  }
`;

const MessageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  cursor: pointer;
  flex-wrap: wrap;

  & .subject {
    flex: 1;
    min-width: 0;
    color: white;
    font-weight: 600;
    font-size: 15px;
  }

  & .meta {
    color: #aaa;
    font-size: 13px;
    flex-shrink: 0;
  }

  & .unread-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: gold;
    flex-shrink: 0;
  }

  & .actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  & .expand-icon {
    color: #aaa;
    font-size: 14px;
  }
`;

const MessageBody = styled.div`
  padding: 0 16px 16px;
  border-top: 1px solid rgba(246, 39, 83, 0.3);
`;

const MessageText = styled.p`
  color: #ddd;
  font-size: 14px;
  line-height: 1.5;
  margin: 12px 0 0;
  white-space: pre-wrap;
`;

const BtnDelete = styled.button`
  padding: 6px 12px;
  font-size: 13px;
  border: 1px solid ${colors.bgColorRed};
  border-radius: 6px;
  background: transparent;
  color: ${colors.bgColorRed};
  cursor: pointer;
  transition: background 0.2s, color 0.2s;

  &:hover {
    background: ${colors.bgColorRed};
    color: white;
  }
`;

const EmptyState = styled.p`
  color: #999;
  font-size: 15px;
  text-align: center;
  padding: 40px 20px;
`;

const LoadingState = styled.p`
  color: #aaa;
  font-size: 15px;
  text-align: center;
  padding: 40px 20px;
`;

const ErrorState = styled.p`
  color: ${colors.bgColorRed};
  font-size: 15px;
  text-align: center;
  padding: 40px 20px;
`;

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function senderLabel(from: Notification['from']): string {
  if (from === false || from == null) return 'Sistema';
  return from?.username ?? 'Sistema';
}

const Messaggi: FC = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  useEffect(() => {
    let cancelled = false;
    const runLoad = async () => {
      try {
        setError(null);
        const res = await apiGetMyNotifications();
        if (cancelled) return;
        setNotifications(res.data ?? []);
      } catch (e) {
        if (cancelled) return;
        setError('Impossibile caricare i messaggi.');
        setNotifications([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    runLoad();
    return () => { cancelled = true; };
  }, []);

  const handleToggleExpand = async (n: Notification) => {
    const isOpening = expandedId !== n.id;
    if (isOpening) {
      setExpandedId(n.id);
      if (n.read === null) {
        try {
          await apiMarkNotificationAsRead(n.id);
          setNotifications((prev) =>
            prev.map((item) => (item.id === n.id ? { ...item, read: new Date().toISOString() } : item))
          );
        } catch {
          // ignore
        }
      }
    } else {
      setExpandedId(null);
    }
  };

  const handleDelete = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    if (!window.confirm('Eliminare questo messaggio?')) return;
    try {
      await apiDeleteNotification(id);
      setNotifications((prev) => prev.filter((item) => item.id !== id));
      if (expandedId === id) setExpandedId(null);
    } catch {
      setError('Impossibile eliminare il messaggio.');
    }
  };

  return (
    <MainContainer>
      {loading && (
        <PageWrapper>
          <LoadingState>Caricamento messaggi...</LoadingState>
        </PageWrapper>
      )}
      {error && !loading && (
        <PageWrapper>
          <ErrorState>{error}</ErrorState>
        </PageWrapper>
      )}
      {!loading && !error && (
      <PageWrapper>
        <PageTitle>Messaggi</PageTitle>
        {notifications.length === 0 ? (
          <EmptyState>Nessun messaggio.</EmptyState>
        ) : (
          <List>
            {notifications.map((n) => (
              <MessageCard key={n.id} $expanded={expandedId === n.id}>
                <MessageHeader onClick={() => handleToggleExpand(n)}>
                  <span className="subject">{parseWrestlerTags(n.subject, navigate, () => {})}</span>
                  <div className="actions">
                    {n.read === null && <span className="unread-dot" title="Non letto" />}
                    <span className="meta">
                      {senderLabel(n.from)} · {formatDate(n.created_at)}
                    </span>
                    <BtnDelete
                      type="button"
                      onClick={(e) => handleDelete(e, n.id)}
                      title="Elimina"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </BtnDelete>
                    <FontAwesomeIcon
                      className="expand-icon"
                      icon={expandedId === n.id ? faChevronUp : faChevronDown}
                    />
                  </div>
                </MessageHeader>
                {expandedId === n.id && (
                  <MessageBody>
                    <MessageText>{parseWrestlerTags(n.text, navigate, () => {})}</MessageText>
                  </MessageBody>
                )}
              </MessageCard>
            ))}
          </List>
        )}
      </PageWrapper>
      )}
    </MainContainer>
  );
};

export default Messaggi;
